import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import * as dat from 'lil-gui'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { DotScreenPass } from 'three/examples/jsm/postprocessing/DotScreenPass.js'
import { HalftonePass } from 'three/examples/jsm/postprocessing/HalftonePass.js'
import { SAOPass } from 'three/examples/jsm/postprocessing/SAOPass.js'
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js'

THREE.ColorManagement.enabled = false

/**
 * Base
 */
// Debug
const gui = new dat.GUI()
const folder1 = gui.addFolder('lights')
folder1.open()
const folder2 = gui.addFolder('house')
const folder3 = gui.addFolder('effect')
const folder4 = gui.addFolder('halfPass')
const folder5 = gui.addFolder('saoPass')

// Canvas
const canvas = document.querySelector('canvas.webgl')



// Scene
const scene = new THREE.Scene()

// Fog
const fog = new THREE.Fog('#262837', 1, 15)
folder3.add(fog, 'near').name('Fog-near').min(0).max(10).step(0.001)
folder3.add(fog, 'far').name('Fog-far').min(0).max(10).step(0.001)
folder3.addColor(fog, 'color').name('Fog-color')

scene.fog = fog
/**
 * Textures
 */
const textureLoader = new THREE.TextureLoader()

const doorColorTexture = textureLoader.load('/textures/door/color.jpg')
const doorAlphaTexture = textureLoader.load('/textures/door/alpha.jpg')
const doorAmbientOcclusionTexture = textureLoader.load('/textures/door/ambientOcclusion.jpg')
const doorHeightTexture = textureLoader.load('/textures/door/height.jpg')
const doorNormalTexture = textureLoader.load('/textures/door/normal.jpg')
const doorMetalnessTexture = textureLoader.load('/textures/door/metalness.jpg')
const doorRoughnessTexture = textureLoader.load('/textures/door/roughness.jpg')

const bricksColorTexture = textureLoader.load('/textures/bricks/color.jpg')
const bricksAmbientOcclusionTexture = textureLoader.load('/textures/bricks/ambientOcclusion.jpg')
const bricksNormalTexture = textureLoader.load('/textures/bricks/normal.jpg')
const bricksRoughnessTexture = textureLoader.load('/textures/bricks/roughness.jpg')

const grassColorTexture = textureLoader.load('/textures/grass/color.jpg')
const grassAmbientOcclusionTexture = textureLoader.load('/textures/grass/ambientOcclusion.jpg')
const grassNormalTexture = textureLoader.load('/textures/grass/normal.jpg')
const grassRoughnessTexture = textureLoader.load('/textures/grass/roughness.jpg')

grassColorTexture.repeat.set(8, 8)
grassAmbientOcclusionTexture.repeat.set(8, 8)
grassNormalTexture.repeat.set(8, 8)
grassRoughnessTexture.repeat.set(8, 8)
// repeat texture 8 times on x and y axis

// repear the texture horizontally
grassColorTexture.wrapS = THREE.RepeatWrapping
grassAmbientOcclusionTexture.wrapS = THREE.RepeatWrapping
grassNormalTexture.wrapS = THREE.RepeatWrapping
grassRoughnessTexture.wrapS = THREE.RepeatWrapping
// repeat the texture vertically
grassColorTexture.wrapT = THREE.RepeatWrapping
grassAmbientOcclusionTexture.wrapT = THREE.RepeatWrapping
grassNormalTexture.wrapT = THREE.RepeatWrapping
grassRoughnessTexture.wrapT = THREE.RepeatWrapping




/**
 * House
 */

// group
const house = new THREE.Group()
scene.add(house)

// walls
const walls = new THREE.Mesh(
    new THREE.BoxGeometry(4, 3, 4),
    new THREE.MeshStandardMaterial({
         //color: '#ac8e82' 
            map: bricksColorTexture,
            aoMap: bricksAmbientOcclusionTexture,
            normalMap: bricksNormalTexture,
            roughnessMap: bricksRoughnessTexture,

        })
)
walls.position.y = 3 / 2
walls.castShadow = false
walls.receiveShadow = true
house.add(walls)

// roof
const roof = new THREE.Mesh(
    new THREE.ConeGeometry(3.5, 1, 4),
    new THREE.MeshStandardMaterial({ color: '#b35f45' })
)
roof.position.y = 3 + 1 / 2
roof.rotation.y = Math.PI * 0.25
roof.castShadow = false
roof.receiveShadow = true
house.add(roof)

// Temporary sphere
const sphere = new THREE.Mesh(
    new THREE.SphereGeometry(1, 32, 32),
    new THREE.MeshStandardMaterial({ roughness: 0.7 })
)
sphere.position.y = 1
//scene.add(sphere)

// Floor
const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(20, 20),
    new THREE.MeshStandardMaterial({ 
        //color: '#a9c388' 
        map: grassColorTexture,
        aoMap: grassAmbientOcclusionTexture,
        normalMap: grassNormalTexture,
        roughnessMap: grassRoughnessTexture,



    })
)
floor.rotation.x = - Math.PI * 0.5
floor.position.y = 0
house.add(floor)

// Door
const door = new THREE.Mesh(
    new THREE.PlaneGeometry(2, 2),
    new THREE.MeshStandardMaterial({ 
        
        map: doorColorTexture,
        transparent: true,
        alphaMap: doorAlphaTexture,
        aoMap: doorAmbientOcclusionTexture, 
        displacementMap: doorHeightTexture,
        displacementScale: 0.1,
        normalMap: doorNormalTexture,
        metalnessMap: doorMetalnessTexture,
        roughnessMap: doorRoughnessTexture,
        metalness: 0.3,
        roughness: 0.3,
        aoMapIntensity: 1,
        envMapIntensity: 1,
        displacementBias: 0,
        transparent: true,
        alphaTest: 0.5,
        side: THREE.DoubleSide,

    }
    )
)
door.position.y = 1
door.position.z = 2 + 0.01
house.add(door)

// Bushes
const bushGeometry = new THREE.SphereGeometry(1, 16, 16)
const bushMaterial = new THREE.MeshToonMaterial({ color: '#89c854' })

const bush1 = new THREE.Mesh(bushGeometry, bushMaterial)
bush1.scale.set(0.5, 0.5, 0.5)
bush1.position.set(0.8, 0.2, 2.2)
bush1.castShadow = true
bush1.receiveShadow = true

const bush2 = new THREE.Mesh(bushGeometry, bushMaterial)
bush2.scale.set(0.25, 0.25, 0.25)
bush2.position.set(1.4, 0.1, 2.1)
bush2.castShadow = true
bush2.receiveShadow = true

const bush3 = new THREE.Mesh(bushGeometry, bushMaterial)
bush3.scale.set(0.4, 0.4, 0.4)
bush3.position.set(- 0.8, 0.1, 2.2)
bush3.castShadow = true
bush3.receiveShadow = true

const bush4 = new THREE.Mesh(bushGeometry, bushMaterial)
bush4.scale.set(0.15, 0.15, 0.15)
bush4.position.set(- 1, 0.05, 2.6)
bush4.castShadow = true
bush4.receiveShadow = true

house.add(bush1, bush2, bush3, bush4)

// Graves
const graves = new THREE.Group()
scene.add(graves)

const graveGeometry = new THREE.BoxGeometry(0.6, 0.8, 0.2)
const graveMaterial = new THREE.MeshStandardMaterial({ color: '#b2b6b1' })

for(let i = 0; i < 50; i++)
{
    const angle = Math.random() * Math.PI * 2
    const radius = 3 + Math.random() * 6
    const x = Math.sin(angle) * radius
    const z = Math.cos(angle) * radius

    // Create the mesh
    const grave = new THREE.Mesh(graveGeometry, graveMaterial)

    // Position
    grave.position.set(x, 0.3, z)
    grave.rotation.y = (Math.random() - 0.5) * 0.4
    grave.rotation.z = (Math.random() - 0.5) * 0.4

    //scale
    const scale = 1 + Math.random() * 0.2
    grave.scale.set(scale, scale, scale)

    //shadow
    grave.castShadow = true
    grave.receiveShadow = true


    // Add to the graves container
    graves.add(grave)
}




/**
 * Lights
 */
// Ambient light
const ambientLight = new THREE.AmbientLight('#ffffff', 0)
folder1.add(ambientLight, 'intensity').name('Al-intensity').min(0).max(1).step(0.001)
scene.add(ambientLight)

// Directional light
const moonLight = new THREE.DirectionalLight('#b9d5ff', 0.12)
moonLight.position.set(4, 5, - 2)
moonLight.rotation.z = 0.2
moonLight.rotation.y = 0.2

//gui
folder1.add(moonLight, 'intensity').name('Dl-intensity').min(0).max(1).step(0.001)
folder1.add(moonLight.position, 'x').name('Dl-X').min(- 5).max(5).step(0.001)
folder1.add(moonLight.position, 'y').name('Dl-y').min(- 5).max(5).step(0.001)
folder1.add(moonLight.position, 'z').name('Dl-Z').min(- 5).max(5).step(0.001)
folder1.add(moonLight.rotation, 'z').name('Dl-Rz').min(- 5).max(5).step(0.001)
folder1.add(moonLight.rotation, 'y').name('Dl-Ry').min(- 5).max(5).step(0.001)

scene.add(moonLight)

const moonLightHelper = new THREE.DirectionalLightHelper(moonLight, 0.2)
scene.add(moonLightHelper)
//gui
moonLightHelper.visible = false

folder1.add(moonLightHelper, 'visible').name('moonLight visible')


//door light
const doorLight = new THREE.PointLight('#ff7d46', 1, 7)
doorLight.position.set(0, 2.2, 2.7)
house.add(doorLight)

//Ghost light
const ghost1 = new THREE.PointLight('#262837', 2, 3)
scene.add(ghost1)

const ghost2 = new THREE.PointLight('#262837', 2, 3)
scene.add(ghost2)

const ghost3 = new THREE.PointLight('#262837', 2, 3)
scene.add(ghost3)

//gui
folder2.add(doorLight, 'intensity').name('doorLight-intensity').min(0).max(1).step(0.001)


/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
camera.position.x = 4
camera.position.y = 2
camera.position.z = 5
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true




/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas
})
renderer.outputColorSpace = THREE.LinearSRGBColorSpace
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.setClearColor('#262837')

/**
 * post processing
 */
const effectComposer = new EffectComposer( renderer )
effectComposer.setSize(sizes.width, sizes.height)
effectComposer.setPixelRatio(Math.min(window.devicePixelRatio, 2))  

const renderPass = new RenderPass( scene, camera )
effectComposer.addPass( renderPass )

// DOT SCREEN
const dotScreenPass = new DotScreenPass()
effectComposer.addPass(dotScreenPass)
dotScreenPass.scale = 10
dotScreenPass.enabled = false

folder3.add(dotScreenPass, 'enabled').name('dotScreenPass enabled')
folder3.add(dotScreenPass, 'scale').name('dotScreenPass scale').min(0).max(10).step(0.001)

//harftone
const halftonePass = new HalftonePass( window.innerWidth, window.innerHeight, {
    shape: 1,
    radius: 4,
    rotateR: Math.PI / 12 * 0,
    rotateB: Math.PI / 12 * 2,
    rotateG: Math.PI / 12 * 4,
    scatter: 0,
    blending: 1,
    blendingMode: 1,
    greyscale: true,
    disable: false
} )
effectComposer.addPass(halftonePass)
halftonePass.enabled = false

folder4.add(halftonePass, 'enabled').name('halftonePass enabled')
folder4.add(halftonePass.uniforms.radius, 'value').name('halftonePass radius').min(0).max(10).step(0.001)
folder4.add(halftonePass.uniforms.rotateR, 'value').name('halftonePass rotateR').min(0).max(10).step(0.001)
folder4.add(halftonePass.uniforms.rotateB, 'value').name('halftonePass rotateB').min(0).max(10).step(0.001)
folder4.add(halftonePass.uniforms.rotateG, 'value').name('halftonePass rotateG').min(0).max(10).step(0.001)
folder4.add(halftonePass.uniforms.scatter, 'value').name('halftonePass scatter').min(0).max(10).step(0.001)

//sao
const saoPass = new SAOPass(scene, camera, false, true) 
effectComposer.addPass(saoPass)

saoPass.enabled = false


folder5.add(saoPass, 'enabled').name('saoPass enabled')
folder5.add( saoPass.params, 'output', {
    'Default': SAOPass.OUTPUT.Default,
    'SAO Only': SAOPass.OUTPUT.SAO,
    'Normal': SAOPass.OUTPUT.Normal
} ).onChange( function ( value ) {

    saoPass.params.output = value;

} );
folder5.add( saoPass.params, 'saoBias', - 1, 1 );
folder5.add( saoPass.params, 'saoIntensity', 0, 1 );
folder5.add( saoPass.params, 'saoScale', 0, 10 );
folder5.add( saoPass.params, 'saoKernelRadius', 1, 100 );
folder5.add( saoPass.params, 'saoMinResolution', 0, 1 );
folder5.add( saoPass.params, 'saoBlur' );

//outline
const outlinePass = new OutlinePass(new THREE.Vector2(window.innerWidth, window.innerHeight), scene, camera)
outlinePass.edgeStrength = 10
outlinePass.edgeGlow = 0
outlinePass.edgeThickness = 1
outlinePass.pulsePeriod = 0
outlinePass.visibleEdgeColor.set('#ffffff')
outlinePass.hiddenEdgeColor.set('#190a05')
effectComposer.addPass(outlinePass)
outlinePass.enabled = false

folder5.add(outlinePass, 'enabled').name('outlinePass enabled')
// 				const renderPass = new RenderPass( scene, camera );
// 				const params = {
// 					shape: 1,
// 					radius: 4,
// 					rotateR: Math.PI / 12,
// 					rotateB: Math.PI / 12 * 2,
// 					rotateG: Math.PI / 12 * 3,
// 					scatter: 0,
// 					blending: 1,
// 					blendingMode: 1,
// 					greyscale: false,
// 					disable: false
// 				};
// 				const halftonePass = new HalftonePass( window.innerWidth, window.innerHeight, params );
// 				composer.addPass( renderPass );
// 				composer.addPass( halftonePass );

// 				window.onresize = function () {

// 					// resize composer
// 					renderer.setSize( window.innerWidth, window.innerHeight );
// 					composer.setSize( window.innerWidth, window.innerHeight );
// 					camera.aspect = window.innerWidth / window.innerHeight;
// 					camera.updateProjectionMatrix();


/**
 * Shadows
 */
renderer.shadowMap.enabled = true
moonLight.castShadow = true
doorLight.castShadow = true
ghost1.castShadow = true
ghost2.castShadow = true
ghost3.castShadow = true

walls.castShadow = true
roof.castShadow = true
bush1.castShadow = true
bush2.castShadow = true
bush3.castShadow = true
bush4.castShadow = true
sphere.castShadow = true

floor.receiveShadow = true

doorLight.shadow.mapSize.width = 256
doorLight.shadow.mapSize.height = 256
doorLight.shadow.camera.far = 7
moonLight.shadow.mapSize.width = 256
moonLight.shadow.mapSize.height = 256
moonLight.shadow.camera.far = 7
ghost1.shadow.mapSize.width = 256
ghost1.shadow.mapSize.height = 256
ghost1.shadow.camera.far = 7
ghost2.shadow.mapSize.width = 256
ghost2.shadow.mapSize.height = 256
ghost2.shadow.camera.far = 7
ghost3.shadow.mapSize.width = 256
ghost3.shadow.mapSize.height = 256
ghost3.shadow.camera.far = 7

renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap

folder1.add(floor, 'receiveShadow').name('floor-shadow')

/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()
    //Update ghost
    const ghost1Angle = elapsedTime * 0.5
    ghost1.position.x = Math.cos(ghost1Angle) * 4
    ghost1.position.z = Math.sin(ghost1Angle) * 4
    ghost1.position.y = Math.sin(elapsedTime * 1)

    const ghost2Angle = - elapsedTime * 0.32
    ghost2.position.x = Math.cos(ghost2Angle) * 5
    ghost2.position.z = Math.sin(ghost2Angle) * 5
    ghost2.position.y = Math.sin(elapsedTime * 4) + Math.sin(elapsedTime * 2.5)

    const ghost3Angle = - elapsedTime * 0.18
    ghost3.position.x = Math.cos(ghost3Angle) * (7 + Math.sin(elapsedTime * 0.32))
    ghost3.position.z = Math.sin(ghost3Angle) * (7 + Math.sin(elapsedTime * 0.5))



    // Update controls
    controls.update()

    // Render
    effectComposer.render()
    //renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()